/**
 * 운영도구 - 올인원(All-in-One) 페이지
 * docs/tools_all.md (v8) 기획서 기반
 *
 * PR1: AntD 셸 + 3열 + 탭 스켈레톤 (완)
 * PR2: 좌측 식별 패널 + useUsbQrReader/useMemberIdentity/PhoneNumpad + identifyLock + §5-1 분기 모달 (현재)
 * 후속 PR: PR3 회원요약+여권 / PR4 QR스티커 / PR5 알림톡 / PR6 은행+가드 / PR7 회귀
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ConfigProvider,
  theme as antdTheme,
  App as AntApp,
  Tabs,
  Tooltip,
  Button,
  Spin,
  Alert,
  Descriptions,
  Tag,
  Drawer,
  Empty,
  Modal,
} from 'antd';
import koKR from 'antd/locale/ko_KR';
import {
  ArrowLeftOutlined,
  QrcodeOutlined,
  SearchOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  PrinterOutlined,
  BankOutlined,
  MessageOutlined,
  CameraOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import StoreBadge from '../components/StoreBadge';
import PrinterStatusBar from '../components/PrinterStatusBar';
import QRScanner from '../components/QRScanner';
import PhoneNumpad from '../components/PhoneNumpad';
import useUsbQrReader from '../hooks/useUsbQrReader';
import useMemberIdentity from '../hooks/useMemberIdentity';
import SummaryTab from '../components/AllInOne/SummaryTab';
import PassportTab from '../components/AllInOne/PassportTab';
import StickerTab from '../components/AllInOne/StickerTab';
import AlimtalkTab from '../components/AllInOne/AlimtalkTab';
import BankTab from '../components/AllInOne/BankTab';
import { registerMemberOnly, registerPassport, subscribePending } from '../services/api';
import './AllInOnePage.css';
import './BankWorkspace.css';

// 아직 현장 운영에 사용하지 않는 탭은 기능 코드를 유지한 채 목록에서만 숨긴다.
// QR스티커·알림톡을 함께 공개할 때 이 값을 true 로 바꾸면 된다.
const SHOW_DEFERRED_TABS = false;
// 회원요약을 다시 사용할 때 true 로 바꾸면 된다.
const SHOW_MEMBER_SUMMARY_TAB = false;
const SHOW_SESSION_LOG = false;
const DEFAULT_TAB_KEY = 'bank';

const TAB_DEFS = [
  { key: 'bank',     label: '주렁은행', icon: <BankOutlined /> },
  { key: 'summary',  label: '회원요약', icon: <IdcardOutlined />, hidden: !SHOW_MEMBER_SUMMARY_TAB },
  { key: 'passport', label: '여권',     icon: <SafetyCertificateOutlined /> },
  { key: 'sticker',  label: 'QR스티커', icon: <PrinterOutlined />, deferred: true },
  { key: 'alimtalk', label: '알림톡',   icon: <MessageOutlined />, deferred: true },
];

const VISIBLE_TAB_DEFS = TAB_DEFS.filter(
  (tab) => !tab.hidden && (SHOW_DEFERRED_TABS || !tab.deferred),
);

function AllInOneInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { modal } = AntApp.useApp();

  const id = useMemberIdentity();
  const {
    scannedMember, primary, family, passportMap,
    loading, identifyLock, error, ticketNoMember,
    identifyByQR, identifyByPhone,
    refresh, reset,
    hasUnacknowledgedChanges, hasPendingWriteAction, acknowledgeAndReset,
    markDirty, beginAction, endAction,
  } = id;

  const [activeTab, setActiveTab] = useState(DEFAULT_TAB_KEY);
  const [passportHistoryTarget, setPassportHistoryTarget] = useState(null);
  const [passportHistoryOpen, setPassportHistoryOpen] = useState(false);
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  // 미등록 번호 안내 팝업 — 인라인 안내는 숫자패드를 화면 밖으로 밀어내서 모달로 뺐다.
  //
  // 🔴 **반드시 "검색이 끝난 결과"로만 연다.** 이전 구현은 `[error, phoneDigits]` 만 보고
  //    열었는데, 검색 실패로 남은 `error` 가 그대로인 상태에서 **새 번호를 8자리까지 입력하는
  //    순간** 조건이 성립해 서버에 묻지도 않고 "미등록" 팝업이 떴다.
  //    실제 사고(2026-08-21): 01022574280 조회 404 → 이어서 01022576427 입력만 했는데 팝업이
  //    떴고, 정작 01022576427 요청은 api 로그에 **한 건도 없었다**(취소 후 재조회하니 200).
  //
  // 가드 3겹:
  //   ① `phoneSearchedRef` — `handlePhoneSubmit` 이 조회한 8자리를 적어 둔다. 비어 있으면
  //      "검색으로 생긴 error 가 아니다" → 열지 않는다(QR 스캔 실패 등도 여기서 걸린다).
  //   ② `!loading` — 검색이 끝난 뒤에만 판단한다.
  //   ③ `phoneSearchedRef.current === phoneDigits` — 그새 번호를 고쳤으면 옛 결과다 → 무시.
  //   연 뒤에는 ref 를 비워 [취소] 후 자릿수를 만져도 되살아나지 않게 한다.
  const [noMemberOpen, setNoMemberOpen] = useState(false);
  const phoneSearchedRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (error !== '미등록 회원') return;
    if (phoneSearchedRef.current === null) return;
    if (phoneSearchedRef.current !== phoneDigits) return;
    phoneSearchedRef.current = null;
    setNoMemberOpen(true);
  }, [error, loading, phoneDigits]);
  const LOG_KEY = 'allinone_session_log';
  const LOG_MAX = 200;
  // 페이지 진입(=올인원 마운트) 시 항상 로그 초기화 — 이전 회원의 기록을 보여주지 않음
  const [sessionLog, setSessionLog] = useState(() => {
    try { localStorage.removeItem(LOG_KEY); } catch { /* noop */ }
    return [];
  });

  // 작업 로그 패널 접힘 상태 — 화면이 좁아 기본 접힘, 선택은 localStorage 에 기억(로그 내용과 별개 키).
  const LOG_COLLAPSE_KEY = 'allinone_log_collapsed';
  const [logCollapsed, setLogCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(LOG_COLLAPSE_KEY);
      return v === null ? true : v === '1'; // 기본 접힘
    } catch { return true; }
  });
  const toggleLog = useCallback(() => {
    setLogCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(LOG_COLLAPSE_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, []);

  // 전역 API 펜딩 카운터 — 로딩 중에는 인터랙션 차단
  const [apiPending, setApiPending] = useState(0);
  const [justCompleted, setJustCompleted] = useState(false);
  const completedTimerRef = useRef(null);
  useEffect(() => {
    return subscribePending((count) => {
      setApiPending((prev) => {
        if (prev > 0 && count === 0) {
          if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
          setJustCompleted(true);
          completedTimerRef.current = setTimeout(() => setJustCompleted(false), 600);
        }
        return count;
      });
    });
  }, []);
  useEffect(() => () => {
    if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
  }, []);
  const focusPhoneNumpad = useCallback(() => setPhoneSearchOpen(true), []);
  const appendLog = useCallback((msg) => {
    const ts = new Date().toTimeString().slice(0, 5);
    setSessionLog((prev) => {
      const next = [{ ts, msg, at: Date.now() }, ...prev].slice(0, LOG_MAX);
      try { localStorage.setItem(LOG_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);
  const clearLog = useCallback(() => {
    setSessionLog([]);
    try { localStorage.removeItem(LOG_KEY); } catch { /* noop */ }
  }, []);

  const identified = Boolean(scannedMember);

  // §5-1 — 단일 진입점 handleAnyInput (§12 #28-a: in-flight → unacknowledged 순차 체인)
  const tryIdentify = useCallback((runIdentify) => {
    if (identifyLock) return; // 1차 게이트

    const askUnacknowledged = (after) => {
      modal.confirm({
        title: '이 회원의 변경사항을 확인하셨나요?',
        content: '미확인 변동이 있습니다 — 우측 작업 로그에서 내역을 확인하세요.',
        okText: '확인했음 · 새 회원 조회',
        cancelText: '돌아가기',
        onOk: () => { acknowledgeAndReset(); after(); },
      });
    };

    if (scannedMember) {
      const pending = hasPendingWriteAction();
      if (pending) {
        modal.confirm({
          title: '진행 중 업무를 취소하고 새 회원을 조회할까요?',
          okText: '취소하고 조회',
          cancelText: '계속 작업',
          onOk: () => {
            // 28-a: in-flight 확인 후 미확인 변동도 있으면 체인
            if (hasUnacknowledgedChanges) askUnacknowledged(runIdentify);
            else runIdentify();
          },
        });
        return;
      }
      if (hasUnacknowledgedChanges) {
        askUnacknowledged(runIdentify);
        return;
      }
    }
    runIdentify();
  }, [identifyLock, scannedMember, hasPendingWriteAction, hasUnacknowledgedChanges, acknowledgeAndReset, modal]);

  // 식별 성공 시 카메라 자동 OFF (오작동/연속 스캔 방지)
  useEffect(() => {
    if (scannedMember && cameraOn) setCameraOn(false);
  }, [scannedMember, cameraOn]);

  const handleScannedCode = useCallback((code) => {
    tryIdentify(() => identifyByQR(code));
  }, [tryIdentify, identifyByQR]);

  /**
   * 미등록 번호 → 가입(+선택적으로 여권 구매).
   *
   * 알림톡은 **서버가 각 API 안에서 보낸다** — 여기서 따로 부르지 않는다.
   *   · 가입 → `visit/manual-welcome` 이 신규가입 QR 알림톡 발송
   *   · 여권 → `passport/register` 가 여권 구매 알림톡 + 쿠폰 패키지 발급
   * 전화번호 없이 여권을 산 고객을 사후에 구제하는 동선이라, 두 알림톡이 모두 나가야 한다.
   *
   * 🔴 여권 실패 시 **가입은 되돌리지 않는다**(사용자 결정 2026-08-21). 이미 SIGNUP 주화와
   *    QR 알림톡이 나간 뒤라 완전한 롤백이 불가능하고, 회원 자체는 정상 자산이기 때문이다.
   *    오퍼레이터는 여권 탭에서 재시도하면 된다.
   */
  const runRegisterFlow = useCallback(async (withPassport) => {
    if (phoneDigits.length !== 8) return;
    const phone = '010' + phoneDigits;
    const storeId = localStorage.getItem('store_id') || '3';
    const actionId = `reg-${Date.now()}`;
    beginAction(actionId);
    try {
      const res = await registerMemberOnly(phone, storeId);
      const newMember = res?.member || res?.data?.member;
      if (!newMember) {
        appendLog(`❌ 가입 실패: 서버 응답 이상`);
        return;
      }
      const label = newMember.name || newMember.nickname || phone;
      appendLog(`✅ 신규가입 + 알림톡: ${label}`);

      if (withPassport) {
        const memberId = newMember.member_id || res?._member_id;
        if (!memberId) {
          appendLog(`⚠️ 여권 발급 건너뜀: member_id 미확인 — 여권 탭에서 발급해 주세요`);
        } else {
          try {
            const issueDate = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD (KST 로컬)
            const pp = await registerPassport({ memberId, storeId, issueDate });
            if (pp?.success === false) throw new Error(pp.message || '여권 발급 실패');
            appendLog(`✅ 여권 구매 + 알림톡: ${label}`);
          } catch (ppErr) {
            // 가입은 확정된 상태 — 여권만 실패했음을 분명히 알린다.
            appendLog(`❌ 여권 발급 실패(가입은 완료): ${ppErr?.message || ''} — 여권 탭에서 재시도`);
          }
        }
      }

      await identifyByPhone(phoneDigits);
      setPhoneDigits('');
      setActiveTab(SHOW_DEFERRED_TABS ? 'alimtalk' : DEFAULT_TAB_KEY);
    } catch (err) {
      appendLog(`❌ 가입 실패: ${err?.message || ''}`);
    } finally {
      endAction(actionId);
    }
  }, [phoneDigits, beginAction, endAction, identifyByPhone, appendLog]);

  const handleRegisterUnregistered = useCallback(() => {
    setNoMemberOpen(false);
    runRegisterFlow(false);
  }, [runRegisterFlow]);
  const handleRegisterWithPassport = useCallback(() => {
    setNoMemberOpen(false);
    runRegisterFlow(true);
  }, [runRegisterFlow]);

  const handlePhoneSubmit = () => {
    if (phoneDigits.length !== 8) return;
    const digits = phoneDigits;
    // 이번 조회 대상을 적어 둔다 — 이 값이 있어야만 결과 팝업이 열린다(위 useEffect 가드 ①).
    phoneSearchedRef.current = digits;
    tryIdentify(async () => {
      const member = await identifyByPhone(digits);
      if (member) { setPhoneDigits(''); setPhoneSearchOpen(false); }
    });
  };

  // USB / 네이티브 QR
  useUsbQrReader({
    enabled: !identifyLock && !passportHistoryOpen,
    onScan: handleScannedCode,
  });

  // useMemo — phoneDigits/apiPending 등 식별과 무관한 state 변경 시
  // 탭 자식들이 불필요하게 재렌더되지 않도록 안정화 (숫자패드 입력 지연 방지)
  const tabContent = useMemo(() => ({
    summary: (
      <SummaryTab
        scannedMember={scannedMember} primary={primary} family={family} passportMap={passportMap}
        refresh={refresh} markDirty={markDirty}
        beginAction={beginAction} endAction={endAction} onLog={appendLog}
      />
    ),
    passport: (
      <PassportTab
        historyActionTarget={activeTab === 'passport' ? passportHistoryTarget : null}
        onHistoryOpenChange={setPassportHistoryOpen}
        scannedMember={scannedMember} primary={primary} family={family} passportMap={passportMap}
        refresh={refresh} markDirty={markDirty}
        beginAction={beginAction} endAction={endAction} onLog={appendLog}
      />
    ),
    sticker: (
      <StickerTab
        scannedMember={scannedMember} family={family}
        beginAction={beginAction} endAction={endAction} onLog={appendLog}
      />
    ),
    alimtalk: (
      <AlimtalkTab
        scannedMember={scannedMember}
        beginAction={beginAction} endAction={endAction} onLog={appendLog}
      />
    ),
    bank: (
      <BankTab
        scanPaused={identifyLock || loading}
        phoneSearchAction={<Button icon={<SearchOutlined />} onClick={focusPhoneNumpad} disabled={identifyLock} data-testid="btn-open-phone-search">전화번호 검색</Button>}
        nextMemberAction={<Button icon={<ReloadOutlined />} disabled={identifyLock} onClick={() => { reset(); setPhoneDigits(''); setActiveTab(DEFAULT_TAB_KEY); clearLog(); }} data-testid="btn-next-member">다른 회원 검색</Button>}
        scannedMember={scannedMember} primary={primary} family={family} passportMap={passportMap}
        refresh={refresh} markDirty={markDirty}
        beginAction={beginAction} endAction={endAction} onLog={appendLog}
      />
    ),
  }), [
    scannedMember, primary, family, passportMap,
    refresh, markDirty, beginAction, endAction, appendLog, identifyLock, loading,
    focusPhoneNumpad, reset, clearLog, activeTab, passportHistoryTarget,
  ]);

  const tabItems = useMemo(() => VISIBLE_TAB_DEFS.map(t => ({
    key: t.key,
    label: (
      <Tooltip title={!identified ? '회원 검색 후 사용해 주세요' : ''}>
        <span data-testid={`tab-${t.key}`}>
          {t.icon} {t.key === 'bank' ? '은행' : t.label}
        </span>
      </Tooltip>
    ),
    disabled: !identified,
    children: (
      <div className="tab-pane" data-testid={`tab-content-${t.key}`}>
        {tabContent[t.key] || (
          <div className="tab-placeholder">
            <h3>{t.label}</h3>
            <p>이 탭은 후속 PR 에서 구현됩니다.</p>
          </div>
        )}
      </div>
    ),
  })), [identified, tabContent]);

  return (
    <div className="bank-workspace-page" data-testid="all-in-one-page">
      {(apiPending > 0 || justCompleted) && (
        <div
          className={`api-busy-overlay${apiPending === 0 ? ' done' : ''}`}
          data-testid="api-busy-overlay"
          aria-busy={apiPending > 0}
        >
          <div className="api-busy-card">
            {apiPending > 0 ? (
              <>
                <Spin size="large" />
                <div className="api-busy-text">서버 응답 대기 중… ({apiPending})</div>
                <div className="api-busy-sub">완료될 때까지 화면을 조작하지 마세요</div>
              </>
            ) : (
              <>
                <div className="api-busy-check">✅</div>
                <div className="api-busy-text">완료</div>
              </>
            )}
          </div>
        </div>
      )}
      <header className="all-header">
        <div className="all-header-left">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            data-testid="btn-back-to-menu"
          >
            메뉴
          </Button>
          <span className="all-header-title">
            주렁은행
          </span>

          <PrinterStatusBar />
        </div>
        <div className="all-header-right">
          <span className="bank-history-action-slot" ref={setPassportHistoryTarget} />
          {localStorage.getItem('store_name') && <span className="bank-store-badge"><i aria-hidden="true" />{localStorage.getItem('store_name')}</span>}
          <span className="all-user-name"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>

      <main className="all-main">
        <aside className="all-pane all-pane-left" data-testid="pane-identify">

          {loading && (
            <div style={{ textAlign: 'center', padding: 12 }}>
              <Spin tip="조회 중..." />
            </div>
          )}

          {error && !loading && (
            error === '미등록 회원' ? (
              /* 🔴 인라인은 **한 줄 요약만** — 버튼·설명을 여기 두면 블록이 길어져
                 아래 숫자패드가 화면 밖으로 밀린다(현장 지적 2026-08-21).
                 실제 조작은 아래 미등록 회원 모달이 담당한다. */
              <div className="identify-callout identify-callout-info" data-testid="alert-identify-error">
                <div className="identify-callout-head">
                  <span className="identify-callout-icon">ℹ️</span>
                  <span className="identify-callout-title">
                    미등록 회원 · 010-{phoneDigits.slice(0, 4)}-{phoneDigits.slice(4)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="identify-callout identify-callout-error" data-testid="alert-identify-error">
                <div className="identify-callout-head">
                  <span className="identify-callout-icon">⚠️</span>
                  <span className="identify-callout-title">{error}</span>
                </div>
              </div>
            )
          )}

          {ticketNoMember && (
            <div className="identify-callout identify-callout-info" data-testid="alert-ticket-no-member">
              <div className="identify-callout-head">
                <span className="identify-callout-icon">ℹ️</span>
                <span className="identify-callout-title">회원가입 필요</span>
              </div>
              <div className="identify-callout-desc">
                {ticketNoMember.message || '티켓에 회원이 연결되어 있지 않습니다.'}
                <div style={{ marginTop: 4, fontSize: 12, color: '#546E7A' }}>
                  전화번호 검색을 눌러 010 뒤 8자리를 입력 후 [가입 + 알림톡] 버튼을 누르세요.
                </div>
              </div>
              <Button
                block
                size="large"
                type="primary"
                onClick={focusPhoneNumpad}
                data-testid="btn-ticket-goto-phone"
                className="identify-callout-action"
              >
                가입 후 진행
              </Button>
            </div>
          )}

          {/* 카메라 QR 섹션 — USB QR 스캐너 우선 사용 정책으로 숨김 (코드는 유지)
          <div className="identify-section">
            <div className="identify-section-title">📷 카메라 QR</div>
            <Button
              size="small"
              type={cameraOn ? 'default' : 'primary'}
              icon={<CameraOutlined />}
              block
              onClick={() => setCameraOn((v) => !v)}
              disabled={identifyLock}
              data-testid="btn-toggle-camera"
            >
              {cameraOn ? '카메라 끄기' : '카메라 켜기'}
            </Button>
            {cameraOn && (
              <div className="camera-wrap">
                <QRScanner
                  enabled={cameraOn && !identifyLock && !scannedMember}
                  onScan={(code) => handleScannedCode(code)}
                />
              </div>
            )}
          </div>
          */}



        </aside>

        <section className="zb-shell-workspace" data-testid="pane-workspace">
          <Tabs
            className="zb-category-tabs"
            tabPosition="left"
            animated={{ inkBar: false, tabPane: false }}
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </section>

        {SHOW_SESSION_LOG && (
          <aside
            className={`all-pane all-pane-right${logCollapsed ? ' is-collapsed' : ''}`}
            data-testid="pane-session-log"
          >
            {logCollapsed ? (
              <button
                type="button"
                className="session-log-toggle is-collapsed"
                onClick={toggleLog}
                title="작업 로그 펼치기"
                aria-label="작업 로그 펼치기"
                aria-expanded={false}
                data-testid="session-log-toggle"
              >
                <span className="session-log-toggle-arrow">◀</span>
                <span className="session-log-toggle-label">작업 로그</span>
                {sessionLog.length > 0 && (
                  <span className="session-log-toggle-badge">{sessionLog.length}</span>
                )}
              </button>
            ) : (
              <>
                <div className="pane-title pane-title-log">
                  <span>작업 로그</span>
                  <button
                    type="button"
                    className="session-log-toggle is-expanded"
                    onClick={toggleLog}
                    title="작업 로그 접기"
                    aria-label="작업 로그 접기"
                    aria-expanded
                    data-testid="session-log-toggle"
                  >▶</button>
                </div>
                {sessionLog.length === 0 ? (
                  <div className="pane-placeholder">아직 활동 없음</div>
                ) : (
                  <ul className="session-log-list" data-testid="session-log-list">
                    {sessionLog.slice(0, 20).map((e, i) => (
                      <li key={i} className="session-log-item">
                        <span className="session-log-ts">{e.ts}</span>
                        <span className="session-log-msg">{e.msg}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </aside>
        )}
      </main>

      <Modal open={phoneSearchOpen && !noMemberOpen} onCancel={() => setPhoneSearchOpen(false)} footer={null} title="전화번호로 회원 검색" centered width={520} destroyOnHidden className="bank-phone-modal">
        <PhoneNumpad digits={phoneDigits} onChange={setPhoneDigits} onSubmit={handlePhoneSubmit} disabled={identifyLock} submitLabel="조회" />
      </Modal>
      {/* 미등록 번호 안내 — 조작은 전부 여기서 한다(인라인은 한 줄 요약만). */}
      <Modal
        open={noMemberOpen}
        onCancel={() => setNoMemberOpen(false)}
        footer={null}
        centered
        width={620}
        maskClosable={false}
        /* 닫으면 내용을 DOM 에서 제거한다 — 숨김만 하면 이전 번호가 붙은 버튼이 남는다.
           antd 5.29 기준 `destroyOnClose` 는 deprecated, `destroyOnHidden` 이 정식이다. */
        destroyOnHidden
        title={null}
        className="no-member-modal"
        data-testid="modal-no-member"
      >
        <div className="no-member-body">
          <div className="no-member-title">미등록 회원</div>
          <div className="no-member-phone" data-testid="no-member-phone">
            010-{phoneDigits.slice(0, 4)}-{phoneDigits.slice(4)}
          </div>
          <div className="no-member-desc">
            가입하면 신규가입 알림톡이 발송됩니다.<br />
            <b>전화번호 없이 여권을 구매</b>한 경우 [가입+여권+알림톡]을 눌러 주세요.
          </div>
          <div className="no-member-actions">
            <Button
              type="primary"
              onClick={handleRegisterUnregistered}
              data-testid="btn-register-and-alimtalk"
              className="no-member-btn"
            >
              가입+알림톡
            </Button>
            <Button
              type="primary"
              onClick={handleRegisterWithPassport}
              data-testid="btn-register-passport-and-alimtalk"
              className="no-member-btn"
            >
              가입+여권+알림톡
            </Button>
            <Button
              onClick={() => setNoMemberOpen(false)}
              data-testid="btn-no-member-cancel"
              className="no-member-btn no-member-btn-cancel"
            >
              취소
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default function AllInOnePage() {
  return (
    <ConfigProvider
      locale={koKR}
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#007800',
          colorBgContainer: '#ffffff',
          colorBgElevated: '#ffffff',
          colorBgLayout: '#f5f5f5',
          colorText: '#0d0d0d',
          colorTextSecondary: '#666666',
          colorBorder: '#dcdcdc',
          colorSuccess: '#189018',
          borderRadius: 6,
          fontFamily: '"Noto Sans KR", sans-serif',
        },
      }}
    >
      <AntApp>
        <AllInOneInner />
      </AntApp>
    </ConfigProvider>
  );
}
